import {
    suspensions,
    competencies,
    restrictions,
    breathalyserTestsForSite,
    groupCompetenciesByOrGroup
} from 'areas/competence/domain';

import CardConstants from '../constants';
import {
    isStringSet,
    isNotNullOrEmpty,
    isEmptyObject,
    isDefined
} from 'utilities/is';

// Returns a list of division IDs that the card holder is suspended in
const divisionBlocks = suspensions =>
    suspensions
        .filter(s => isStringSet(s.divisionID))
        .map(s => s.divisionID);

// Returns a list ofsite codes that the card holder is suspended in
const siteBlocks = suspensions =>
    suspensions
        .filter(s => isStringSet(s.siteCode))
        .map(s => s.siteCode);

// Predicate for national level suspensions
const nationalPredicate = s =>
(!isStringSet(s.divisionID) && !isStringSet(s.siteName) &&
    CardConstants.NATIONALLY_SUSPENDED_IDS.indexOf(s.reasonLookupID.toUpperCase()) < 0)

// Predicate for division level suspensions
const divisionPredicate = divisionID => s => s.divisionID === divisionID;

// Site level suspensions
const sitePredicate = siteCode => s => String(s.siteCode) === String(siteCode);

// Predicate for subscription expiries
const subscriptionExpiredPredicate = s => isStringSet(s.reasonLookupID) && CardConstants.SUBSCRIPTION_EXPIRED_ID === s.reasonLookupID.toUpperCase();

// Predicate for pending subscription
const subscriptionPendingPredicate = s => isStringSet(s.reasonLookupID) && CardConstants.SUBSCRIPTION_PENDING_ID === s.reasonLookupID.toUpperCase();

// Returns true if the card has at least one suspension matching the predicate
const hasSuspension = (card, predicate) => card.suspensions.filter(predicate).length > 0;

// Returns a value indicating whether the card holder has a national level suspension
const isNationallySuspended = card => hasSuspension(card, nationalPredicate);

// Returns true if the card holder has a suspension in the given division
const isSuspendedByDivision = (card, divisionID) => hasSuspension(card, divisionPredicate(divisionID));

// Returns true if the card holder has a suspension in the given site
const isSuspendedBySite = (card, siteCode) => hasSuspension(card, sitePredicate(siteCode));

// Returns a value indicating whether the card holder has an expired subscription
const isSubscriptionExpired = card => hasSuspension(card, subscriptionExpiredPredicate);

// Returns a value indicating whether the card holder has an expired subscription
const isSubscriptionPending = card => hasSuspension(card, subscriptionPendingPredicate);

const expiryDate = (card) => card.expiryDate ? new Date(card.expiryDate) : null;

// Returns true if the card expiry is set and before today
const hasExpired = (card) => card.expiryDate && card.expiryDate < new Date();

const processJobRoles = (card) => {
    const jobRolesArray = [];

    if (isStringSet(card.jobRoles)) {

        const jobRoles = card.jobRoles.split('\\n');

        for (let x = 0; x < jobRoles.length; x++) {
            let roleData = jobRoles[x].split('|');
            let roleName = roleData[0];
            let isSafetyCritical = roleData[1];
            let isInvalid = roleData[2] === 'Invalid';
            let isPrimaryJobRole = roleData[3] === 'Primary';
            let jobRoleID = roleData[4];
            let division = roleData[5];
            let isSuspended = roleData[6] === 'Suspended';
            let divisionID = roleData[7];
            let employerID = roleData[8];
            let employerName = roleData[9];

            let job = {
                name: roleName,
                id: jobRoleID,
                isSafetyCritical,
                isInvalid,
                isPrimaryJobRole,
                isSuspended,
                division,
                divisionID,
                employerID,
                employerName,

                displayName: () => {
                    return `${job.name} (${job.division})`;
                }
            };

            jobRolesArray.push(job);
        }
    }

    return jobRolesArray;
};

const processEmployerData = (card) => {
    if (!card) return null;

    const employersArray = [];

    if (isStringSet(card.employer)) {
        employersArray.push({
            id: card.employerID,
            name: card.employer
        });
    }
    if (isStringSet(card.secondaryEmployers) && isStringSet(card.secondaryEmployerIDs)) {
        const employerNames = card.secondaryEmployers.split(',');
        const employerIds = card.secondaryEmployerIDs.split(',');

        for (let i = 0; i < employerNames.length; i++) {
            employersArray.push({
                id: employerIds[i],
                name: employerNames[i]
            });
        }
    }

    return employersArray;
};

const missingCompetences = (competences, groupFilter, missingFilter) => {
    const missing = [];

    const groupedCompetences = groupCompetenciesByOrGroup((competences || []).filter(groupFilter));

    Object.keys(groupedCompetences).forEach(key => {

        const competenceGroup = groupedCompetences[key];
        const isOrGroup = key !== "__ungrouped__";

        const missed = competenceGroup.filter(missingFilter);

        if ((missed.length > 0 && !isOrGroup) || (isOrGroup && missed.length === competenceGroup.length))
            missing.push(missed);
    });

    return missing;

};

const validateDivisionRequirements = (card, locationConfig, location) => {

    if (!(location && location.division))
        return [];

    const groupFilter = d => d.divisionID === location.division.divisionID;
    const competences = locationConfig.divisionRequirements || [];

    const missingFilter =
        dc => {

            let missing = false;
            if (isNotNullOrEmpty(dc.competenceID))
                missing = card.competencies.findIndex(cc => {
                    if (String(cc.alias) === dc.alias) {
                        if ((dc.divisionSpecific === false || dc.divisionID === cc.divisionID)
                            && (dc.companySpecific === false || dc.companyID === cc.companyID)) {
                            return true;
                        }
                    }

                    return false;
                }) === -1;

            if (isNotNullOrEmpty(dc.jobRoleID))
                missing = card.jobRolesArray.findIndex(jr => jr.id.toLowerCase() === dc.jobRoleID.toLowerCase()) === -1;

            return missing;
        }

    return missingCompetences(competences, groupFilter, missingFilter);
};

const validateProjectRequirements = (card, locationConfig, location) => {

    if (!(location && location.project))
        return [];

    const competences = (locationConfig.projectRequirements || []);
    const groupFilter = p => p.projectID === location.project.projectID;
    const missingFilter =
        pc => {

            let missing = false;
            if (isNotNullOrEmpty(pc.competenceID))
                missing = card.competencies.findIndex(cc => {
                    if (String(cc.alias) === pc.alias) {
                        if ((pc.projectSpecific === false || pc.projectID === cc.projectID)
                            && (pc.divisionSpecific === false || pc.divisionID === cc.divisionID)
                            && (pc.companySpecific === false || pc.companyID === cc.companyID)) {
                            return true;
                        }
                    }
                    return false;
                }) === -1;

            if (isNotNullOrEmpty(pc.jobRoleID))
                missing = card.jobRolesArray.findIndex(jr => jr.id.toLowerCase() === pc.jobRoleID.toLowerCase()) === -1;

            return missing;
        }

    return missingCompetences(competences, groupFilter, missingFilter);
};

const validateSiteRequirements = (card, locationConfig, location) => {
    if (!(location && location.site)) {
        return [];
    }

    const competences = locationConfig.siteRequirements || [];
    const groupFilter = site => site.siteCode === location.site.siteCode;
    const missingFilter =
        sc => {
            let missing = false;
            if (isNotNullOrEmpty(sc.competenceID))
                missing = card.competencies.findIndex(cc => {
                    if (String(cc.alias) === sc.alias) {
                        if ((sc.siteSpecific === false || sc.siteCode === String(cc.siteCode) || String(cc.siteCode) === "NSS")
                            && (sc.companySpecific === false || sc.companyID === cc.companyID)
                            && (sc.divisionSpecific === false || sc.divisionID === cc.divisionID)) {
                            return true;
                        }
                    }
                    return false;
                }) === -1;

            if (isNotNullOrEmpty(sc.jobRoleID)) {
                missing = card.jobRolesArray.findIndex(jr => jr.id.toLowerCase() === sc.jobRoleID.toLowerCase()) === -1;
            }
            return missing;
        }

    return missingCompetences(competences, groupFilter, missingFilter);
};

const validateZoneRequirements = (card, locationConfig, location) => {
    if (!(location && location.zone)) {
        return [];
    }

    const competences = locationConfig.zoneRequirements || [];
    const groupFilter = zc => zc.zoneCode === location.zone.zoneCode && zc.siteCode === location.site.siteCode;
    const missingFilter =
        zc => {
            let missing = false;
            if (isNotNullOrEmpty(zc.competenceID))
                missing = card.competencies.findIndex(cc => {
                    if (String(cc.alias) === zc.alias) {
                        if ((zc.siteSpecific === false || zc.siteCode === String(cc.siteCode))
                            && (zc.zoneSpecific === false || zc.zoneCode === String(cc.zoneCode))
                            && (zc.companySpecific === false || zc.companyID === cc.companyID)) {
                            return true;
                        }
                    }
                    return false;
                }) === -1;

            if (isNotNullOrEmpty(zc.jobRoleID)) {
                missing = card.jobRolesArray.findIndex(jr => jr.id.toLowerCase() === zc.jobRoleID.toLowerCase()) === -1;
            }
            return missing;
        };

    return missingCompetences(competences, groupFilter, missingFilter);
};

export function processCard(sourceCard, location, locationConfig, systemConfig) {
    if (!sourceCard || isEmptyObject(sourceCard)) {
        return null;
    }

    let card = {
        ...sourceCard.cardData
    };

    if (isDefined(sourceCard.fatigueInformation)) {
        card.fatigueInformation = sourceCard.fatigueInformation;
    }

    card.jobRolesArray = processJobRoles(card);
    card.employersArray = processEmployerData(card);

    card.selectedEmployerID = card.employersArray.length >= 1 ? card.employersArray[0].id : card.selectedEmployerID;

    if (location && location.division && !isStringSet(card.selectedJobRoleID) && isStringSet(location.division.divisionJobRoleID)) {
        if (card.jobRolesArray.filter(j => j.id.toLowerCase() === location.division.divisionJobRoleID).length > 0) {
            card.selectedJobRoleID = location.division.divisionJobRoleID;
        }
    }

    card.competencies = competencies(sourceCard.competenceData);
    card.suspensions = suspensions(sourceCard.competenceData);
    card.divisionBlocks = divisionBlocks(card.suspensions);
    card.siteBlocks = siteBlocks(card.suspensions);
    card.restrictions = restrictions(sourceCard.competenceData);
    card.breathalyserTestsForSite =
        location && location.site && isStringSet(location.site.siteCode)
            ? breathalyserTestsForSite(sourceCard.competenceData, location.site.siteCode)
            : [];

    card.serialNumber = sourceCard.serialNumber;
    card.photo = sourceCard.photo;
    card.cardRender = sourceCard.card;
    card.expiryDate = expiryDate(card);

    card.isExpired = hasExpired(card);
    card.isSuspended = isStringSet(card.suspendedDate);
    card.isCancelled = isStringSet(card.cancelledDate);

    card.isSubscriptionExpired = isSubscriptionExpired(card);
    card.isSubscriptionPending = isSubscriptionPending(card);
    card.isNationallySuspended = isNationallySuspended(card);
    card.isSuspendedByDivision = location && location.division && isSuspendedByDivision(card, location.division.divisionID);
    card.isSuspendedBySite = location && location.site && isStringSet(location.site.siteCode) && isSuspendedBySite(card, location.site.siteCode);

    card.isFatigued = card.fatigueInformation && card.fatigueInformation.isFatigued;

    card.hasEmployer = isStringSet(card.employer);
    card.hasSponsor = isStringSet(card.sponsor);
    card.hasBreathalyserTestForSite = location && location.site && breathalyserTestsForSite.length > 0;

    card.noEmployer = !card.hasEmployer;

    card.isMinimalCardDisplayShowingBlocks =
        (card.isSuspendedByDivision || card.isSuspendedBySite) && card.hasEmployer;

    card.isMinimalCardDisplay =
        card.isExpired
        || card.isNationallySuspended
        || card.isSuspended
        || card.isCancelled
        || card.noEmployer
        || card.isSubscriptionExpired
        || card.isSubscriptionPending;

    // Ensure the spot check flag is set for access controllers
    card.canPerformSpotCheck = card.canPerformSpotCheck || card.canSwipeCards;
    card.canLogin = card.canPerformSpotCheck || card.canSwipeCards;

    card.displayOnly =
        card.isCancelled || card.isExpired || card.isSuspended
        || card.isSubscriptionExpired || card.isSubscriptionPending || !card.hasEmployer
        || (!card.canSwipeCards && !card.canSelfSwipe && !card.canPerformSpotCheck)
        || card.isNationallySuspended || card.isSuspendedByDivision || card.isSuspendedBySite;

    card.limitedMenu = !card.isSuspended && !card.isCancelled && card.hasEmployer && (card.canSelfSwipe || card.canPerformSpotCheck) && !card.canSwipeCards;
    card.fullMenu = !card.isSuspended && !card.isCancelled && card.hasEmployer && !card.canSelfSwipe && !card.canPerformSpotCheck;

    if (!card.hasEmployer) {
        card.warningMessage = "No Employer";
    } else if (card.isNationallySuspended) {
        card.warningMessage = "Blocked Nationally";
    } else if (card.isSuspendedByDivision) {
        card.warningMessage = `Blocked in current ${systemConfig.divisionLevelName.toLowerCase()}: ${location.division.divisionName}`;
    } else if (card.isSuspendedBySite) {
        card.warningMessage = `Blocked in current ${systemConfig.siteLevelName.toLowerCase()}: ${location.site.siteName}`;
    } else if (card.isSubscriptionExpired) {
        card.warningMessage = "Subscription Expired";
    } else if (card.isSubscriptionPending) {
        card.warningMessage = "Subscription Pending";
    } else if (card.isSuspended) {
        card.warningMessage = "Card Suspended";
    } else if (card.isCancelled) {
        card.warningMessage = "Card Cancelled";
    } else if (card.isExpired) {
        card.warningMessage = "Card Expired";
    } else if (card.isFatigued) {
        card.warningMessage = "Worker Fatigued"
    }

    card.missingDivisionRequirements = validateDivisionRequirements(card, locationConfig, location);
    card.missingProjectRequirements = validateProjectRequirements(card, locationConfig, location);
    card.missingSiteRequirements = validateSiteRequirements(card, locationConfig, location);
    card.missingZoneRequirements = validateZoneRequirements(card, locationConfig, location);

    card.missingCompetences = false;

    if (isNotNullOrEmpty(card.missingDivisionRequirements)
        || isNotNullOrEmpty(card.missingProjectRequirements)
        || isNotNullOrEmpty(card.missingSiteRequirements)
        || isNotNullOrEmpty(card.missingZoneRequirements)) {

        card.missingCompetences = true;
    } else {
        card.missingCompetences = false;
    }

    return card;
}